/**
 * Chat 多模型对话模块状态管理
 * 管理会话、子会话、对话窗口状态（用于 /chat 页面的多窗口对话功能）
 * 注意：与 stores/chat.ts（角色聊天）是不同的功能模块
 * 
 * 设计原则：代码适配数据，直接使用 API 返回的数据结构
 */
import { defineStore } from 'pinia'
import { useNuxtApp } from '#imports'
import { useI18n } from '~/composables/useI18n'
import { getCachedMessages } from '~/locales'
import type { ChatDialogueState, DialogueCount, LocalChatRecord } from '@/types/chat'
import { createChatRecordFromResponse } from '@/types/chat'
import {
    createConversationApi,
    createSubConversationApi,
    deleteSubConversationApi,
    getChatRecordsApi,
    getConversationDetailApi,
    sendChatMessageApi,
    type SendChatMessageResponse,
} from '~/api/chat'
import { useAppStore } from './app'
import { useUserStore } from './user'
import { useNotificationRtm } from '~/composables/useNotificationRtm'
import { emitter } from '~/utils/emitter'

/**
 * 获取当前团队信息
 */
const getCurrentTeam = (): { id?: string } => {
    if (import.meta.client) {
        try {
            return JSON.parse(localStorage.getItem('currentTeam') || '{}')
        } catch {
            return {}
        }
    }
    return {}
}



// ---- 对话窗口启用状态（仅当前页面会话内有效，不做本地持久化）----
// 说明：用户需求为“仅当前会话/当前页面生效”，因此不从 localStorage 恢复，也不写入 localStorage。
// 这里仍保留一个会话内初始化标志，避免在同一页面内切换会话时复用上一次的禁用状态。
let _dialogueEnabledSessionInitialized = false

// 发送兜底：当某个窗口缺失 subConversationId 时，按需创建子会话（避免并发重复创建）
const _ensureSubConversationIdPromises = new Map<string, Promise<number | null>>()

type ChatRequestError = {
    data?: {
        desc?: string
        message?: string
    }
    message?: string
}

const getRequestErrorMessage = (error: unknown): string => {
    if (!error || typeof error !== 'object') return ''

    const requestError = error as ChatRequestError
    return requestError.data?.desc || requestError.data?.message || requestError.message || ''
}

/**
 * Chat Dialogue Store 状态接口
 */
interface ChatDialogueStoreState {
    /** 当前会话 ID（加密字符串） */
    conversationId: string | undefined
    /** 当前会话系统提示词（System Instructions） */
    systemPrompt: string
    /** 最近一次接口失败文案 */
    lastErrorMessage: string
    /** 对话窗口状态列表 */
    dialogueStates: ChatDialogueState[]
    /** 侧边栏刷新触发器（递增计数，tool.vue watch 此值触发 ChatSidebar 刷新） */
    sidebarRefreshTrigger: number
    /** Workbench 积分刷新触发器（保存系统提示词后递增） */
    creditsRefreshTrigger: number

}

/**
 * Chat Dialogue Store
 * 用于 /chat 页面的多窗口对话功能
 */
export const useChatDialogueStore = defineStore('chatDialogue', {
    state: (): ChatDialogueStoreState => ({
        conversationId: undefined,
        systemPrompt: '',
        lastErrorMessage: '',
        dialogueStates: [],
        sidebarRefreshTrigger: 0,
        creditsRefreshTrigger: 0,
    }),

    getters: {
        /**
         * 当前窗口数量
         */
        dialogueCount(): DialogueCount {
            return Math.min(Math.max(this.dialogueStates.length, 1), 3) as DialogueCount
        },
    },

    actions: {
        /**
         * 确保指定窗口拥有 subConversationId
         * - 某些历史会话可能返回 sub_conversations=[]
         * - 或首个窗口被初始化为 subConversationId=undefined
         * 发送时需要子会话ID，因此在发送前按需创建子会话并回填到 dialogueStates[dialogueIndex]
         */
        async ensureSubConversationId(dialogueIndex: number): Promise<number | null> {
            const dialogue = this.dialogueStates[dialogueIndex]
            if (!dialogue) return null

            const existing = dialogue.subConversationId
            if (typeof existing === 'number' && Number.isFinite(existing) && existing > 0) {
                return existing
            }

            if (!this.conversationId) return null

            const key = `${this.conversationId}:${dialogueIndex}`
            const inflight = _ensureSubConversationIdPromises.get(key)
            if (inflight) return await inflight

            const p = (async () => {
                try {
                    const currentTeam = getCurrentTeam()
                    const appStore = useAppStore()
                    const res = await createSubConversationApi({
                        conversation_id: this.conversationId!,
                        team_id: currentTeam?.id || '',
                        product_id: appStore.currentProject?.id,
                        platform: 'WEB',
                        public_visible: true,
                        copyright_protection: false,
                    })

                    const subConvId = res.data?.sub_conversations?.[0]?.id
                    if (res.status === 200 && typeof subConvId === 'number' && subConvId > 0) {
                        // 若对话框期间发生了窗口重排/删除，重新取一次引用再赋值
                        const latest = this.dialogueStates[dialogueIndex]
                        if (latest) latest.subConversationId = subConvId
                        return subConvId
                    }
                } catch (e) {
                    console.error('[ChatDialogueStore] ensureSubConversationId 失败:', e)
                }
                return null
            })()

            _ensureSubConversationIdPromises.set(key, p)
            try {
                return await p
            } finally {
                _ensureSubConversationIdPromises.delete(key)
            }
        },
        /**
         * 获取默认模型标识符（避免与已使用的模型重复）
         * @param index 窗口索引
         * @param usedModels 已被其他窗口使用的模型列表
         * @returns 模型标识符（如 'GPT_5_2'），用于存储和 API 调用
         */
        getDefaultModel(index: number, usedModels: string[] = []): string {
            const appStore = useAppStore()
            const models = appStore.chatModelData
            if (models && models.length > 0) {
                // 单窗口场景下，优先使用首页工作台最近一次选择的模型。
                // 这是一个单向同步来源：首页 -> Chat，避免 Chat 页面反向污染首页选择。
                const workbenchPreferredModel = appStore.workbenchChatSelectedModel
                if (
                    index === 0 &&
                    workbenchPreferredModel &&
                    !usedModels.includes(workbenchPreferredModel) &&
                    models.some(m => m.model === workbenchPreferredModel)
                ) {
                    return workbenchPreferredModel
                }

                // 优先按 index 取模型
                const preferredModel = models[index % models.length]?.model || ''
                // 如果首选模型未被使用，直接返回
                if (!usedModels.includes(preferredModel)) {
                    return preferredModel
                }
                // 否则找一个未被使用的模型
                const availableModel = models.find(m => !usedModels.includes(m.model))
                return availableModel?.model || preferredModel
            }
            return ''
        },

        /**
         * 用当前可用模型校正各窗口已选模型。
         * 模型列表是异步拉取的：接口慢或首次失败时，窗口会用空列表初始化，
         * selectedModel 落空后不会自愈，头部只剩兜底图标（表现为「无模型被选中」）。
         * 列表到位后需要补齐空值、替换已下线模型，并尽量避免窗口间重复占用。
         */
        syncDialogueModelsWithAvailable() {
            const appStore = useAppStore()
            const models = appStore.chatModelData || []
            if (models.length === 0) return

            const usedModels: string[] = []
            this.dialogueStates.forEach((dialogue, idx) => {
                const isUsable =
                    Boolean(dialogue.selectedModel) &&
                    models.some(m => m.model === dialogue.selectedModel) &&
                    !usedModels.includes(dialogue.selectedModel)

                if (!isUsable) {
                    const nextModel = this.getDefaultModel(idx, usedModels)
                    if (nextModel) dialogue.selectedModel = nextModel
                }

                if (dialogue.selectedModel) usedModels.push(dialogue.selectedModel)
            })
        },

        /**
         * 加载子会话的聊天记录（首次加载）
         * 直接存储 API 返回的数据，不做格式转换
         */
        async loadChatRecords(dialogueIndex: number, subConversationId: number) {
            const dialogue = this.dialogueStates[dialogueIndex]
            if (!dialogue) return

            dialogue.isLoading = true

            try {
                const currentTeam = getCurrentTeam()
                const appStore = useAppStore()

                const res = await getChatRecordsApi({
                    teamId: currentTeam?.id || '',
                    productId: appStore.currentProject?.id,
                    subConversationId,
                    offset: 0,
                    pageSize: 50,
                })

                if (res.status === 200 && res.data) {
                    // 直接使用 API 返回的数据，按时间正序排列
                    const records = res.data.records || []
                    dialogue.records = records.sort((a: LocalChatRecord, b: LocalChatRecord) =>
                        a.create_time - b.create_time
                    )
                    dialogue.nextOffset = res.data.next_offset
                    // 有 nextOffset 说明还有更多数据
                    dialogue.hasMore = res.data.next_offset !== undefined && res.data.next_offset !== null
                }
            } catch (error) {
                console.error('[ChatDialogueStore] 加载聊天记录失败:', error)
            } finally {
                dialogue.isLoading = false
            }
        },

        /**
         * 加载更多历史消息（向上滚动加载）
         * @returns 是否成功加载了更多数据
         */
        async loadMoreRecords(dialogueIndex: number): Promise<boolean> {
            const dialogue = this.dialogueStates[dialogueIndex]
            if (!dialogue || dialogue.subConversationId == null) return false

            // 检查是否可以加载更多
            if (dialogue.isLoadingMore || !dialogue.hasMore || dialogue.nextOffset === undefined) {
                return false
            }

            dialogue.isLoadingMore = true

            try {
                const currentTeam = getCurrentTeam()
                const appStore = useAppStore()

                const res = await getChatRecordsApi({
                    teamId: currentTeam?.id || '',
                    productId: appStore.currentProject?.id,
                    subConversationId: dialogue.subConversationId,
                    offset: dialogue.nextOffset,
                    pageSize: 50,
                })

                if (res.status === 200 && res.data) {
                    const newRecords = res.data.records || []

                    if (newRecords.length > 0) {
                        // 按时间排序后，前置追加（历史消息在前面）
                        const sortedNewRecords = newRecords.sort((a: LocalChatRecord, b: LocalChatRecord) =>
                            a.create_time - b.create_time
                        )
                        dialogue.records = [...sortedNewRecords, ...dialogue.records]
                    }

                    dialogue.nextOffset = res.data.next_offset
                    dialogue.hasMore = res.data.next_offset !== undefined && res.data.next_offset !== null && newRecords.length > 0

                    return newRecords.length > 0
                }
            } catch (error) {
                console.error('[ChatDialogueStore] 加载更多记录失败:', error)
            } finally {
                dialogue.isLoadingMore = false
            }

            return false
        },

        /**
         * 初始化新对话状态
         */
        initNewConversation() {
            this.conversationId = undefined
            this.systemPrompt = ''
            this.dialogueStates = [{
                index: 0,
                subConversationId: undefined,
                enabled: true,
                selectedModel: this.getDefaultModel(0),
                records: [],
                isLoading: false,
                isLoadingMore: false,
                hasMore: true,
            }]
        },

        /** 从 localStorage 恢复当前会话各窗口启用状态（仅首次进入会话时使用） */
        restoreDialogueEnabledFromStorage(conversationId: string) {
            // no-op (session-only)
            void conversationId
        },

        /** 持久化当前会话各窗口启用状态 */
        persistDialogueEnabledToStorage(conversationId?: string) {
            // no-op (session-only)
            void conversationId
        },

        /** 设置单个窗口启用状态（并持久化） */
        setDialogueEnabled(index: number, enabled: boolean) {
            const dialogue = this.dialogueStates[index]
            if (!dialogue) return
            dialogue.enabled = !!enabled
            // session-only: do not persist
        },

        /** 将所有对话窗口恢复为启用（用于页面卸载清理） */
        resetAllDialoguesEnabled() {
            this.dialogueStates.forEach(d => {
                d.enabled = true
            })
            _dialogueEnabledSessionInitialized = false
        },

        /**
         * 设置会话 ID 并加载数据
         * @returns 'ok' 成功 | 'not_found' 会话不存在（8001）| undefined 其他情况
         */
        async setConversationId(id: string | undefined): Promise<'ok' | 'not_found' | undefined> {
            const prevConversationId = this.conversationId
            // 确保模型数据已加载（在初始化对话窗口之前）
            const appStore = useAppStore()
            if (!appStore.chatModelData || appStore.chatModelData.length === 0) {
                // 模型列表失败不能阻断会话初始化，否则整个对话区都渲染不出来；
                // 列表稍后到位时由 syncDialogueModelsWithAvailable 补齐窗口模型。
                try {
                    await appStore.getChatModelList()
                } catch (e) {
                    console.warn('[ChatDialogueStore] 加载 Chat 模型列表失败:', e)
                }
            }

            // 如果 ID 相同且已有数据，不重复加载
            if (id === this.conversationId && this.dialogueStates.length > 0) {
                return 'ok'
            }

            this.conversationId = id

            if (!id) {
                this.initNewConversation()
                return 'ok'
            }

            // 获取会话详情，包含子会话列表
            try {
                const res = await getConversationDetailApi(id)

                if (res.status === 200 && res.data) {
                    this.systemPrompt = res.data.system_prompt || ''
                    const subConversations = res.data.sub_conversations || []

                    if (subConversations.length === 0) {
                        // 没有子会话，初始化一个空窗口
                        this.dialogueStates = [{
                            index: 0,
                            subConversationId: undefined,
                            enabled: true,
                            selectedModel: this.getDefaultModel(0),
                            records: [],
                            isLoading: false,
                            isLoadingMore: false,
                            hasMore: true,
                        }]
                        // 会话内：首次进入可按需恢复（当前为 no-op）；切换会话：强制全开
                        if (!_dialogueEnabledSessionInitialized) {
                            this.restoreDialogueEnabledFromStorage(String(id))
                            _dialogueEnabledSessionInitialized = true
                        } else if (prevConversationId && prevConversationId !== id) {
                            this.dialogueStates.forEach(d => { d.enabled = true })
                        }
                        return
                    }

                    // 根据子会话列表初始化对话窗口（最多3个）
                    const subConvList = subConversations.slice(0, 3)
                    // 收集已使用的模型，避免重复分配
                    const usedModels: string[] = []
                    const availableModelList = appStore.chatModelData || []
                    // 模型列表未就绪时不能判定「已下线」：必须先保留后端原模型，
                    // 等列表到位后再由 syncDialogueModelsWithAvailable 校验/纠偏。
                    const modelListReady = availableModelList.length > 0
                    this.dialogueStates = subConvList.map((subConv: { id: number; create_time: number; model?: string }, idx: number) => {
                        // 优先使用接口返回的模型，如果为空、重复或已下线则回退到可用模型
                        let selectedModel = subConv.model || ''
                        if (modelListReady) {
                            if (
                                !selectedModel ||
                                usedModels.includes(selectedModel) ||
                                !availableModelList.some(m => m.model === selectedModel)
                            ) {
                                selectedModel = this.getDefaultModel(idx, usedModels)
                            }
                        }
                        if (selectedModel) usedModels.push(selectedModel)
                        return {
                            index: idx,
                            subConversationId: subConv.id,
                            enabled: true,
                            selectedModel,
                            records: [],
                            isLoading: true,
                            isLoadingMore: false,
                            hasMore: true,
                        }
                    })

                    // 并行加载每个子会话的聊天记录
                    await Promise.all(
                        subConvList.map((subConv: { id: number }, idx: number) =>
                            this.loadChatRecords(idx, subConv.id)
                        )
                    )

                    // 会话内：首次进入可按需恢复（当前为 no-op）；切换会话：强制全开
                    if (!_dialogueEnabledSessionInitialized) {
                        this.restoreDialogueEnabledFromStorage(String(id))
                        _dialogueEnabledSessionInitialized = true
                    } else if (prevConversationId && prevConversationId !== id) {
                        this.dialogueStates.forEach(d => { d.enabled = true })
                    }
                } else if (res.status === 8001) {
                    this.conversationId = undefined
                    this.systemPrompt = ''
                    this.initNewConversation()
                    return 'not_found'
                } else {
                    // 其他 API 调用失败，初始化空窗口
                    this.systemPrompt = ''
                    this.dialogueStates = [{
                        index: 0,
                        subConversationId: undefined,
                        enabled: true,
                        selectedModel: this.getDefaultModel(0),
                        records: [],
                        isLoading: false,
                        isLoadingMore: false,
                        hasMore: true,
                    }]
                    if (!_dialogueEnabledSessionInitialized) {
                        this.restoreDialogueEnabledFromStorage(String(id))
                        _dialogueEnabledSessionInitialized = true
                    } else if (prevConversationId && prevConversationId !== id) {
                        this.dialogueStates.forEach(d => { d.enabled = true })
                    }
                }
            } catch (error) {
                console.error('[ChatDialogueStore] 获取会话详情失败:', error)
                // 发生错误时初始化空窗口
                this.systemPrompt = ''
                this.dialogueStates = [{
                    index: 0,
                    subConversationId: undefined,
                    enabled: true,
                    selectedModel: this.getDefaultModel(0),
                    records: [],
                    isLoading: false,
                    isLoadingMore: false,
                    hasMore: true,
                }]
                if (!_dialogueEnabledSessionInitialized) {
                    this.restoreDialogueEnabledFromStorage(String(id))
                    _dialogueEnabledSessionInitialized = true
                } else if (prevConversationId && prevConversationId !== id) {
                    this.dialogueStates.forEach(d => { d.enabled = true })
                }
            }
        },

        /**
         * 创建新会话
         * @param forceNew 是否强制创建新会话（用于"新建聊天"按钮）
         * @returns 创建成功返回 true，失败返回 false
         */
        async createConversation(forceNew: boolean = false): Promise<boolean> {
            // 非强制模式下，如果已有会话则直接返回
            if (!forceNew && this.conversationId) return true
            this.lastErrorMessage = ''

            try {
                const currentTeam = getCurrentTeam()
                const appStore = useAppStore()


                const res = await createConversationApi({
                    team_id: currentTeam?.id || '',
                    product_id: appStore.currentProject?.id,
                    platform: 'WEB',
                    public_visible: true,
                    copyright_protection: false,
                })

                if (res.status === 200 && res.data) {
                    this.conversationId = res.data.conversation_id
                    this.lastErrorMessage = ''
                    if (forceNew) this.systemPrompt = ''

                    // 强制创建时，重置对话窗口状态
                    if (forceNew) {
                        this.dialogueStates = [{
                            index: 0,
                            subConversationId: res.data.sub_conversation_id,
                            enabled: true,
                            selectedModel: this.getDefaultModel(0),
                            records: [],
                            isLoading: false,
                            isLoadingMore: false,
                            hasMore: true,
                        }]
                        // 新会话：强制全开并写入持久化（避免刷新后恢复到旧状态）
                        _dialogueEnabledSessionInitialized = true
                    } else if (this.dialogueStates[0]) {
                        // 非强制模式，更新第一个窗口的 subConversationId
                        this.dialogueStates[0].subConversationId = res.data.sub_conversation_id
                    }
                    return true
                }

                this.lastErrorMessage = res.desc || res.message || ''
            } catch (error) {
                this.lastErrorMessage = getRequestErrorMessage(error)
                console.error('[ChatDialogueStore] 创建会话失败:', error)
            }
            return false
        },

        /**
         * 新增对话窗口（创建子会话）
         * @returns 'success' 成功 | 'need_conversation' 需要先发送消息 | 'failed' 失败
         */
        async addDialogue(): Promise<'success' | 'need_conversation' | 'failed'> {
            if (this.dialogueStates.length >= 3) return 'failed'

            const newIndex = this.dialogueStates.length
            this.lastErrorMessage = ''


            // 没有会话 ID 时，先创建会话
            if (!this.conversationId) {
                const success = await this.createConversation()
                if (!success) {
                    return 'failed'
                }
            }

            // 此时一定有 conversationId，调用 API 创建子会话
            try {
                const currentTeam = getCurrentTeam()
                const appStore = useAppStore()

                const res = await createSubConversationApi({
                    conversation_id: this.conversationId!,
                    team_id: currentTeam?.id || '',
                    product_id: appStore.currentProject?.id,
                    platform: 'WEB',
                    public_visible: true,
                    copyright_protection: false,
                })

                // API 返回结构：{ sub_conversations: [{ id: number }] }
                const subConvId = res.data?.sub_conversations?.[0]?.id

                if (res.status === 200 && subConvId) {
                    this.lastErrorMessage = ''
                    // 收集已使用的模型，避免重复分配
                    const usedModels = this.dialogueStates.map(d => d.selectedModel).filter(Boolean)
                    this.dialogueStates.push({
                        index: newIndex,
                        subConversationId: subConvId,
                        enabled: true,
                        selectedModel: this.getDefaultModel(newIndex, usedModels),
                        records: [],
                        isLoading: true,  // 设置为加载中
                        isLoadingMore: false,
                        hasMore: true,
                    })

                    // 加载后端复制过来的聊天记录
                    await this.loadChatRecords(newIndex, subConvId)

                    // 同步窗口1正在生成的内容到新窗口
                    this.syncStreamingRecords(newIndex)

                    return 'success'
                }

                this.lastErrorMessage = res.desc || res.message || ''
            } catch (error) {
                this.lastErrorMessage = getRequestErrorMessage(error)
                console.error('[ChatDialogueStore] 创建子会话失败:', error)
            }

            return 'failed'
        },

        /**
         * 移除指定索引的对话窗口（删除子会话）
         * 只有在 API 删除成功后才从本地删除
         */
        async removeDialogue(index: number): Promise<boolean> {
            if (this.dialogueStates.length <= 1) return false
            if (index < 0 || index >= this.dialogueStates.length) return false

            const dialogue = this.dialogueStates[index]

            // 如果有子会话 ID，调用 API 删除
            if (dialogue?.subConversationId) {
                try {
                    const res = await deleteSubConversationApi(dialogue.subConversationId)
                    // API 删除失败，不执行本地删除
                    if (res.status !== 200) {
                        console.error('[ChatDialogueStore] 删除子会话失败: API 返回状态码', res.status)
                        return false
                    }
                } catch (error) {
                    console.error('[ChatDialogueStore] 删除子会话失败:', error)
                    // API 调用失败，不执行本地删除
                    return false
                }
            }

            // API 删除成功，从数组中移除
            this.dialogueStates.splice(index, 1)

            // 重新计算索引
            this.dialogueStates.forEach((d, idx) => {
                d.index = idx
            })

            return true
        },

        /**
         * 设置对话窗口数量
         * @param count 目标数量
         * @returns 'success' 成功 | 'need_conversation' 需要先发送消息 | 'failed' 失败
         */
        async setDialogueCount(count: DialogueCount): Promise<'success' | 'need_conversation' | 'failed'> {
            const currentCount = this.dialogueStates.length

            if (count === currentCount) return 'success'

            if (count > currentCount) {
                // 增加窗口
                for (let i = currentCount; i < count; i++) {
                    const result = await this.addDialogue()
                    if (result !== 'success') {
                        return result
                    }
                }
            } else {
                // 减少窗口（从后往前删除）
                for (let i = currentCount - 1; i >= count; i--) {
                    await this.removeDialogue(i)
                }
            }
            return 'success'
        },

        /**
         * 更新对话窗口的模型
         * @param index 窗口索引
         * @param modelId 模型标识符
         */
        updateDialogueModel(index: number, modelId: string) {
            if (this.dialogueStates[index]) {
                this.dialogueStates[index].selectedModel = modelId
            }
        },

        /**
         * 添加聊天记录到指定窗口
         * @param index 窗口索引
         * @param record 聊天记录（使用 API 数据结构）
         */
        addRecord(index: number, record: LocalChatRecord) {
            if (this.dialogueStates[index]) {
                this.dialogueStates[index].records.push(record)
            }
        },

        /**
         * 更新最后一条记录的 AI 回复内容（流式输出）
         * @param index 窗口索引
         * @param reply AI 回复内容
         * @param isStreaming 是否仍在流式输出
         */
        updateLastRecordReply(index: number, reply: string, isStreaming: boolean = true) {
            const dialogue = this.dialogueStates[index]
            if (dialogue && dialogue.records.length > 0) {
                const lastRecord = dialogue.records[dialogue.records.length - 1]
                if (lastRecord) {
                    lastRecord.reply = reply
                    lastRecord.isStreaming = isStreaming
                }
            }
        },

        /**
         * 根据 subConversationId 和 msgId 更新记录的 AI 回复
         * 用于处理 RTM 消息
         * 会同时更新所有窗口中相同 msg_id 且 isStreaming 的记录
         * 
         * 乐观发送增强：如果精确匹配失败，尝试通过 subConversationId 匹配临时记录
         * （处理 RTM 消息先于 API 响应到达的情况）
         * 
         * @param subConversationId 子会话ID（用于备用匹配）
         * @param msgId 消息ID
         * @param reply AI 回复内容
         * @param isStreaming 是否仍在流式输出
         */
        updateRecordByMsgId(
            subConversationId: number,
            msgId: number,
            reply: string,
            isStreaming: boolean
        ) {
            let updated = false

            console.log('[ChatDialogueStore] updateRecordByMsgId 调用:', {
                subConversationId,
                msgId,
                replyLength: reply.length,
                isStreaming,
                dialogueCount: this.dialogueStates.length
            })

            // 遍历所有窗口，更新所有相同 msg_id 且正在生成的记录
            for (let i = 0; i < this.dialogueStates.length; i++) {
                const dialogue = this.dialogueStates[i]
                if (!dialogue) continue

                // 1. 首先尝试精确匹配 msg_id
                let record = dialogue.records.find(r => r.msg_id === msgId && r.isStreaming)

                // 2. 如果精确匹配失败，尝试备用匹配：
                //    - 同一 subConversationId 的窗口
                //    - msg_id < 0（临时记录）且 isStreaming
                //    这用于处理 RTM 消息先于 API 响应到达的情况
                if (!record && dialogue.subConversationId === subConversationId) {
                    record = dialogue.records.find(r => r.msg_id < 0 && r.isStreaming)
                    if (record) {
                        console.log(`[ChatDialogueStore] 窗口${i} 备用匹配成功，更新临时记录 msg_id:`, {
                            oldMsgId: record.msg_id,
                            newMsgId: msgId
                        })
                        // 同时更新 msg_id 为真实 ID
                        record.msg_id = msgId
                    }
                }

                console.log(`[ChatDialogueStore] 窗口${i} 查找 msg_id=${msgId}:`, {
                    found: !!record,
                    allMsgIds: dialogue.records.map(r => r.msg_id),
                    allStreamingStatus: dialogue.records.map(r => ({ msgId: r.msg_id, isStreaming: r.isStreaming }))
                })

                if (record) {
                    // 拼接模式：将新内容追加到现有回复后面
                    record.reply = (record.reply || '') + reply
                    record.isStreaming = isStreaming
                    updated = true
                    console.log(`[ChatDialogueStore] 窗口${i} 已更新，当前长度:`, record.reply.length)
                }
            }

            if (!updated) {
                console.warn('[ChatDialogueStore] 未找到对应的记录, msgId:', msgId, 'subConversationId:', subConversationId)
            }
        },

        /**
         * 同步窗口1正在生成的内容到指定窗口
         * 用于创建新窗口时，确保正在生成的内容也能显示
         * @param targetIndex 目标窗口索引
         */
        syncStreamingRecords(targetIndex: number) {
            const sourceDialogue = this.dialogueStates[0]  // 窗口1
            const targetDialogue = this.dialogueStates[targetIndex]

            console.log('[ChatDialogueStore] syncStreamingRecords 调用:', {
                targetIndex,
                sourceExists: !!sourceDialogue,
                targetExists: !!targetDialogue
            })

            if (!sourceDialogue || !targetDialogue) return

            // 获取窗口1中正在生成的记录
            const streamingRecords = sourceDialogue.records.filter(r => r.isStreaming)

            console.log('[ChatDialogueStore] 窗口1正在生成的记录:', {
                count: streamingRecords.length,
                msgIds: streamingRecords.map(r => r.msg_id)
            })

            for (const streamingRecord of streamingRecords) {
                // 在新窗口中找到对应的记录（通过 msg_id 匹配）
                const targetRecord = targetDialogue.records.find(
                    r => r.msg_id === streamingRecord.msg_id
                )

                console.log('[ChatDialogueStore] 同步记录:', {
                    msgId: streamingRecord.msg_id,
                    replyLength: streamingRecord.reply.length,
                    targetRecordExists: !!targetRecord
                })

                if (targetRecord) {
                    // 已有记录，用窗口1的内容覆盖
                    targetRecord.reply = streamingRecord.reply
                    targetRecord.isStreaming = true
                    console.log('[ChatDialogueStore] 已更新目标记录')
                } else {
                    // 没有记录，复制整条记录到新窗口
                    targetDialogue.records.push({
                        ...streamingRecord,
                        isStreaming: true
                    })
                    console.log('[ChatDialogueStore] 已复制记录到新窗口')
                }
            }
        },

        /**
         * 重新生成 AI 回复
         * @param dialogueIdx 窗口索引
         * @param recordIdx 记录索引
         */
        async retryMessage(dialogueIdx: number, recordIdx: number) {
            const dialogue = this.dialogueStates[dialogueIdx]
            if (!dialogue) return
            if (dialogue.enabled === false) {
                emitter.emit('chat-show-enable-dialogue-tip')
                return
            }

            const record = dialogue.records[recordIdx]
            if (!record) return

            const rawMsgId = (record as Partial<LocalChatRecord>).msg_id
            let trackingMsgId =
                typeof rawMsgId === 'number' && Number.isFinite(rawMsgId) && rawMsgId !== 0
                    ? rawMsgId
                    : undefined
            if (trackingMsgId === undefined) {
                trackingMsgId = -Date.now()
                    ; (record as unknown as { msg_id: number }).msg_id = trackingMsgId
            }

            // 清空回复，进入 streaming 状态，重置超时计时
            record.reply = ''
            record.isStreaming = true
            record.streamingStartTime = Date.now()

            // 重新发送（传入 msg_id）
            const modelId = dialogue.selectedModel || record.model
            await this.sendMessage(dialogueIdx, record.input, modelId, trackingMsgId)
        },

        /**
         * 编辑消息并重新发送
         * @param dialogueIdx 窗口索引
         * @param recordIdx 记录索引
         * @param newContent 新的用户输入
         */
        async editAndResend(dialogueIdx: number, recordIdx: number, newContent: string) {
            const dialogue = this.dialogueStates[dialogueIdx]
            if (!dialogue) return
            if (dialogue.enabled === false) {
                emitter.emit('chat-show-enable-dialogue-tip')
                return
            }

            const record = dialogue.records[recordIdx]
            if (!record) return

            const rawMsgId = (record as Partial<LocalChatRecord>).msg_id
            let trackingMsgId =
                typeof rawMsgId === 'number' && Number.isFinite(rawMsgId) && rawMsgId !== 0
                    ? rawMsgId
                    : undefined
            if (trackingMsgId === undefined) {
                trackingMsgId = -Date.now()
                    ; (record as unknown as { msg_id: number }).msg_id = trackingMsgId
            }

            // 更新输入内容，清空回复，重置超时计时
            record.input = newContent
            record.reply = ''
            record.isStreaming = true
            record.streamingStartTime = Date.now()

            // 重新发送（传入 msg_id）
            const modelId = dialogue.selectedModel || record.model
            await this.sendMessage(dialogueIdx, newContent, modelId, trackingMsgId)
        },

        /**
         * 处理发送消息接口返回的 RTM 相关数据
         * @param data 发送消息响应数据
         */
        async handleRtmResponse(data: SendChatMessageResponse) {
            const { disconnect, connect, joinChannel, leaveChannel } = useNotificationRtm()

            // 需要刷新 RTM token 时，更新 userStore 并重新连接
            if (data.need_refresh_rtm_token && data.token) {
                console.log('[ChatDialogueStore] 刷新 RTM token 并重新连接')

                const userStore = useUserStore()
                userStore.updateNotificationRtmToken({
                    token: data.token,
                    channel_names: data.channel_names || [],
                    leave_channel_names: data.leave_channel_names || [],
                })

                // 断开后重新连接（connect 会自动处理频道加入/退出）
                await disconnect()
                await connect()
            } else {
                // 不需要刷新 token，只处理频道加入/退出
                if (data.channel_names?.length) {
                    console.log('[ChatDialogueStore] 加入 RTM 频道:', data.channel_names)
                    for (const channel of data.channel_names) {
                        await joinChannel(channel)
                    }
                }

                if (data.leave_channel_names?.length) {
                    console.log('[ChatDialogueStore] 退出 RTM 频道:', data.leave_channel_names)
                    for (const channel of data.leave_channel_names) {
                        await leaveChannel(channel)
                    }
                }
            }
        },

        /**
         * 发送聊天消息
         * 乐观发送：新消息先创建临时记录立即显示，API 成功后更新 msg_id
         * @param dialogueIndex 窗口索引
         * @param input 用户输入内容
         * @param modelId 使用的模型标识符
         * @param msgId 消息ID（重试/编辑时传入，用于更新已有记录）
         * @returns 发送结果（包含 RTM 频道信息）
         */
        async sendMessage(
            dialogueIndex: number,
            input: string,
            modelId: string,
            msgId?: number
        ): Promise<SendChatMessageResponse | null> {
            const dialogue = this.dialogueStates[dialogueIndex]
            if (!dialogue) {
                console.error('[ChatDialogueStore] 窗口不存在:', dialogueIndex)
                return null
            }
            if (dialogue.enabled === false) {
                emitter.emit('chat-show-enable-dialogue-tip')
                return null
            }

            const realMsgId =
                typeof msgId === 'number' && Number.isFinite(msgId) && msgId > 0 ? msgId : undefined

            // 检查必要参数
            if (!this.conversationId) {
                console.error('[ChatDialogueStore] 会话ID不存在')
                return null
            }

            // 兜底：历史会话可能没有子会话，或该窗口 subConversationId 丢失；这里按需创建
            if (dialogue.subConversationId == null || dialogue.subConversationId <= 0) {
                const ensured = await this.ensureSubConversationId(dialogueIndex)
                if (!ensured) {
                    console.error('[ChatDialogueStore] 子会话ID不存在')
                    return null
                }
            }

            // ========== 乐观发送：新消息先创建临时记录 ==========
            // 使用负数时间戳作为临时 ID，避免与真实 msg_id（正数）冲突
            let tempMsgId: number | null = null
            if (msgId === undefined) {
                tempMsgId = -Date.now()
                // 从模型列表中查找显示名称和图标，保持与 API 返回的记录格式一致
                const appStore = useAppStore()
                const matchedModel = appStore.chatModelData?.find(m => m.model === modelId)
                const tempRecord: LocalChatRecord = {
                    msg_id: tempMsgId,
                    input,
                    input_type: 'TEXT',
                    reply: '',
                    reply_type: 'TEXT',
                    model: matchedModel?.desc || modelId,
                    model_icon: matchedModel?.icon,
                    create_time: Date.now(),
                    isStreaming: true,
                    streamingStartTime: Date.now(),
                }
                this.addRecord(dialogueIndex, tempRecord)
                console.log('[ChatDialogueStore] 乐观发送：创建临时记录', { tempMsgId, dialogueIndex })
            }

            try {
                const currentTeam = getCurrentTeam()
                const appStore = useAppStore()

                const res = await sendChatMessageApi({
                    conversation_id: this.conversationId,
                    sub_conversation_id: this.dialogueStates[dialogueIndex]?.subConversationId as number,
                    team_id: currentTeam?.id || '',
                    input,
                    input_type: 'TEXT',
                    model: modelId,
                    ...(realMsgId !== undefined ? { msg_id: realMsgId } : {}),
                    product_id: appStore.currentProject?.id,
                    platform: 'WEB',
                    public_visible: true,
                    copyright_protection: false,
                })

                if (res.status === 200 && res.data) {
                    const data = res.data as SendChatMessageResponse

                    // 处理积分不足（error_code = 1011）
                    if (data.error_code === 1011) {
                        const { $i18n } = useNuxtApp()
                        if (tempMsgId !== null) {
                            // 乐观发送：更新已有临时记录
                            const record = dialogue.records.find(r => r.msg_id === tempMsgId)
                            if (record) {
                                if (data.msg_id) record.msg_id = data.msg_id
                                record.insufficientCredits = true
                                record.reply = $i18n.t('chat.insufficientCreditsReply') as string
                                record.isStreaming = false
                            }
                        } else if (typeof msgId === 'number') {
                            // 重试/编辑：更新现有记录
                            const record = dialogue.records.find(r => r.msg_id === msgId)
                            if (record) {
                                if (data.msg_id && msgId <= 0) {
                                    this.updateTempRecordMsgId(dialogueIndex, msgId, data.msg_id)
                                }
                                record.insufficientCredits = true
                                record.reply = $i18n.t('chat.insufficientCreditsReply') as string
                                record.isStreaming = false
                            }
                        }
                        return data
                    }

                    // 乐观发送：API 成功后更新临时记录的 msg_id
                    if (data.msg_id && tempMsgId !== null) {
                        this.updateTempRecordMsgId(dialogueIndex, tempMsgId, data.msg_id)
                    } else if (data.msg_id && typeof msgId === 'number' && msgId <= 0) {
                        this.updateTempRecordMsgId(dialogueIndex, msgId, data.msg_id)
                    }

                    // 如果有错误（非积分不足），将错误信息作为 AI 回复显示
                    if (data.has_error && data.error_reply) {
                        if (tempMsgId !== null) {
                            // 乐观发送：更新临时记录
                            const record = dialogue.records.find(r => r.msg_id === tempMsgId)
                            if (record) {
                                if (data.msg_id) record.msg_id = data.msg_id
                                record.reply = data.error_reply
                                record.isStreaming = false
                            }
                        } else if (typeof msgId === 'number') {
                            // 重试/编辑：更新对应记录
                            const record = dialogue.records.find(r => r.msg_id === msgId)
                            if (record) {
                                if (data.msg_id && msgId <= 0) {
                                    this.updateTempRecordMsgId(dialogueIndex, msgId, data.msg_id)
                                }
                                record.reply = data.error_reply
                                record.isStreaming = false
                            }
                        }
                    }

                    // 处理 RTM token 刷新和频道管理
                    await this.handleRtmResponse(data)

                    return data
                }

                // API 返回非 200 状态：标记临时记录发送失败
                if (tempMsgId !== null) {
                    const record = dialogue.records.find(r => r.msg_id === tempMsgId)
                    if (record) {
                        record.sendFailed = true
                        record.isStreaming = false
                    }
                }

                return null
            } catch (error) {
                console.error('[ChatDialogueStore] 发送消息失败:', error)

                // 网络异常处理
                if (tempMsgId !== null) {
                    // 乐观发送：更新已有临时记录，标记发送失败
                    const record = dialogue.records.find(r => r.msg_id === tempMsgId)
                    if (record) {
                        record.sendFailed = true
                        record.isStreaming = false
                    }
                } else if (typeof msgId === 'number') {
                    // 重试/编辑失败：更新现有记录
                    const record = dialogue.records.find(r => r.msg_id === msgId)
                    if (record) {
                        record.sendFailed = true
                        record.isStreaming = false
                    }
                }
                return null
            }
        },

        /**
         * 更新临时记录的 msg_id 为真实 ID
         * 用于乐观发送后 API 响应返回时更新
         * @param dialogueIndex 窗口索引
         * @param tempMsgId 临时消息ID（负数）
         * @param realMsgId 真实消息ID（正数）
         */
        updateTempRecordMsgId(dialogueIndex: number, tempMsgId: number, realMsgId: number) {
            const dialogue = this.dialogueStates[dialogueIndex]
            if (!dialogue) return

            const record = dialogue.records.find(r => r.msg_id === tempMsgId)
            if (record) {
                console.log('[ChatDialogueStore] 更新临时记录 msg_id:', { tempMsgId, realMsgId })
                record.msg_id = realMsgId
            } else {
                // 可能已被 RTM 备用匹配更新，检查是否已有该 realMsgId 的记录
                const existingRecord = dialogue.records.find(r => r.msg_id === realMsgId)
                if (existingRecord) {
                    console.log('[ChatDialogueStore] 临时记录已被 RTM 更新:', { realMsgId })
                } else {
                    console.warn('[ChatDialogueStore] 未找到临时记录:', { tempMsgId, realMsgId })
                }
            }
        },

        /**
         * 重新发送失败的消息
         * @param dialogueIdx 窗口索引
         * @param recordIdx 记录索引
         */
        async resendFailedMessage(dialogueIdx: number, recordIdx: number) {
            const dialogue = this.dialogueStates[dialogueIdx]
            const record = dialogue?.records[recordIdx]
            if (!record || !record.sendFailed) return
            if (dialogue?.enabled === false) {
                emitter.emit('chat-show-enable-dialogue-tip')
                return
            }

            // 清除失败状态，重新发送，设置超时计时
            record.sendFailed = false
            record.isStreaming = true
            record.streamingStartTime = Date.now()
            record.reply = ''

            // 使用当前窗口选中的模型标识符重新发送
            // record.model 存储的是显示名称（如 "GPT-5.2"），不能直接传给 API
            const modelId = dialogue.selectedModel
            await this.sendMessage(dialogueIdx, record.input, modelId)

            // 发送成功后移除临时记录（新记录会通过 API 响应创建）
            // 由于 sendMessage 会创建新记录，我们需要移除这条失败的临时记录
            const idx = dialogue.records.indexOf(record)
            if (idx > -1) {
                dialogue.records.splice(idx, 1)
            }
        },

        /**
         * 检查并关闭超时的 streaming 消息
         * 如果消息超过指定时间未收到完成信号，自动关闭 loading 状态
         * @param timeoutMs 超时时间，默认 3 分钟（180000ms）
         */
        /**
         * 通知侧边栏刷新会话列表（递增触发器，由 tool.vue watch 响应）
         */
        triggerSidebarRefresh() {
            this.sidebarRefreshTrigger++
        },

        /**
         * 通知 Workbench 重新计算 Chat 模式积分
         */
        triggerCreditsRefresh() {
            this.creditsRefreshTrigger++
        },

        checkStreamingTimeout(timeoutMs: number = 3 * 60 * 1000) {
            const now = Date.now()
            let timeoutCount = 0

            for (const dialogue of this.dialogueStates) {
                for (const record of dialogue.records) {
                    if (
                        record.isStreaming &&
                        record.streamingStartTime &&
                        now - record.streamingStartTime > timeoutMs
                    ) {
                        record.isStreaming = false
                        // 如果没有任何回复内容，显示超时提示（使用国际化文案）
                        if (!record.reply) {
                            const { locale } = useI18n()
                            const localeMessages = getCachedMessages(locale.value as any) || getCachedMessages('en')
                            record.reply = localeMessages?.chat?.streamingTimeoutReply || 'Response timed out, please retry'
                        }
                        timeoutCount++
                        console.warn('[ChatDialogueStore] 消息超时，自动关闭 streaming:', {
                            msgId: record.msg_id,
                            elapsed: now - record.streamingStartTime,
                        })
                    }
                }
            }

            if (timeoutCount > 0) {
                console.log(`[ChatDialogueStore] 共 ${timeoutCount} 条消息超时`)
                // 超时关闭后刷新用户余额（与正常完成时一致）
                useUserStore().getBalance()
            }
        },
    },
})
